// Denied & underpaid — in-memory mock store. No network.
import { useSyncExternalStore } from 'react';
import type { AppealSent, AppealSettings, Claim, Outcome, SentStatus } from './types';
import type { Channel } from './payerRules';
import { isMail, ruleForPayerName } from './payerRules';
import { buildAppealSettings, buildAppealsSent, buildDenied, buildUnderpaid, SOFIA_APPEAL } from './fixtures';
import { APPEALS_USER, billedOf, clockNow, collapseCodes, expectedFor, money } from './utils';

export const LATENCY = { call: 1500, askReply: 3000, approve: 3000, confirm: 2500 } as const;

export type Scenario =
  | ''
  | 'empty'
  | 'after-send'
  | 'after-mail'
  | 'dentist-approval'
  | 'patient-form'
  | 'fax-failed'
  | 'close-deadline'
  | 'no-authorization'
  | 'no-signature'
  | 'returned'
  | 'autopilot';

interface State {
  denied: Claim[];
  underpaid: Claim[];
  sent: AppealSent[];
  settings: AppealSettings;
  scenario: Scenario;
}

const listeners = new Set<() => void>();
let version = 0;
function emit(): void {
  version += 1;
  for (const l of listeners) l();
}
const openedOnce = new Set<string>();
const who = `${APPEALS_USER.name} (${APPEALS_USER.org})`;

const SCENARIOS: Scenario[] = [
  '',
  'empty',
  'after-send',
  'after-mail',
  'dentist-approval',
  'patient-form',
  'fax-failed',
  'close-deadline',
  'no-authorization',
  'no-signature',
  'returned',
  'autopilot',
];

function readScenario(): Scenario {
  try {
    const raw = new URLSearchParams(window.location.search).get('scenario') ?? '';
    const match = SCENARIOS.find((s) => s === raw);
    if (match) return match;
  } catch {
    /* no window */
  }
  return '';
}

/** Short dentist name for a signature, e.g. "Dr. Emily Carter" → "Dr. Carter". */
export function shortDentist(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  const last = parts[parts.length - 1] ?? fullName;
  return `Dr. ${last}`;
}

/**
 * Who signs a claim's appeal (D130).
 * Clinical appeals: the treating dentist reads and approves the letter first.
 * Billing and patient's appeals: the practice's chosen billing signer (from
 * Settings), not whoever happened to treat the patient — so a billing appeal on
 * Dr. Brooks's claim is still signed by the chosen billing signer (e.g. Dr. Carter).
 */
export function signerFor(claim: Claim): string {
  if (claim.appealKind === 'clinical') {
    return claim.approvedBy ?? shortDentist(claim.provider);
  }
  const chosen = state.settings.billingSigner;
  if (chosen) return shortDentist(chosen);
  return claim.approvedBy ?? shortDentist(claim.provider);
}

// ---- send rows ----
function statusForFirstChannel(ch: Channel): SentStatus {
  if (isMail(ch)) return 'in_mail';
  if (ch === 'fax') return 'faxed';
  return 'sent';
}

const PRELOADED = buildAppealsSent();

function buildRowForClaim(claim: Claim, initial: SentStatus): AppealSent {
  const rule = ruleForPayerName(claim.payer.name);
  const first = rule.order[0] ?? rule.accepts[0] ?? 'certified_mail';
  const howMap: Record<string, string> = {
    website: rule.firstDestName,
    api: rule.firstDestName,
    secure_message: rule.firstDestName,
    secure_email: `${claim.payer.short}’s secure email`,
    fax: 'Fax',
    certified_mail: 'Certified mail, return receipt',
    certified_mail_plain: 'Certified mail',
    first_class: 'First-class mail',
  };
  return {
    id: claim.id,
    patient: claim.patient,
    payerShort: claim.payer.short,
    codes: collapseCodes(claim.procedures),
    billed: billedOf(claim.procedures),
    sentDate: '2026-10-15',
    sentDisplay: 'Oct 15',
    sentHow: howMap[first] ?? 'Certified mail',
    status: initial,
    happening:
      initial === 'in_mail'
        ? `With USPS now; ELVA tracks it to ${claim.payer.short} and keeps the receipt.`
        : initial === 'faxed'
          ? `Sent to ${claim.payer.short}’s appeals fax. ${claim.payer.short} hasn’t confirmed it yet; ELVA calls to check.`
          : `Sending now on ${rule.firstDestName}.`,
  };
}

function initState(): State {
  const scenario = readScenario();
  const denied = buildDenied();
  const underpaid = buildUnderpaid();
  const full = buildAppealsSent();
  const settings = buildAppealSettings();

  // The default appeals-sent list drops Linda + Michael, who are still in Denied.
  const defaultSent = full.filter((r) => r.id !== 'CLM-0818' && r.id !== 'CLM-0827');

  const st: State = { denied, underpaid, sent: defaultSent, settings, scenario };

  if (scenario === 'empty') {
    st.denied = [];
    st.underpaid = [];
    return st;
  }
  if (scenario === 'after-send' || scenario === 'after-mail') {
    st.denied = denied.filter((c) => c.id !== 'CLM-0818' && c.id !== 'CLM-0827');
    st.sent = full;
    sessionSent.add('CLM-0818');
    sessionSent.add('CLM-0827');
    return st;
  }
  if (scenario === 'dentist-approval') {
    const linda = denied.find((c) => c.id === 'CLM-0818');
    if (linda) linda.approval = 'waiting';
    return st;
  }
  if (scenario === 'patient-form') {
    const linda = denied.find((c) => c.id === 'CLM-0818');
    if (linda) {
      linda.appealKind = 'patient';
      linda.patientForm = 'waiting';
      linda.patientFormDate = 'Oct 14';
    }
    return st;
  }
  if (scenario === 'no-signature') {
    const brooks = settings.dentists.find((d) => d.id === 'brooks');
    if (brooks) {
      brooks.onFile = false;
      delete brooks.adoptedDate;
    }
    return st;
  }
  if (scenario === 'no-authorization') {
    settings.authorization.signed = false;
    settings.autopilot = false;
    return st;
  }
  if (scenario === 'close-deadline') {
    const michael = denied.find((c) => c.id === 'CLM-0827');
    if (michael) {
      michael.needsHuman = true;
      michael.deadline = { date: '2026-10-21', basis: 'Cigna allows 180 days from its decision', verb: 'Appeal' };
    }
    return st;
  }
  if (scenario === 'fax-failed') {
    const row = st.sent.find((r) => r.id === 'CLM-0920');
    if (row) {
      row.status = 'didnt_go';
      row.sentHow = 'Guardian Secure Channel';
      row.happening =
        'Guardian’s fax was busy 3 times, so ELVA sent it through Guardian Secure Channel on Oct 14. Guardian confirms secure messages within a day.';
    }
    return st;
  }
  if (scenario === 'returned') {
    const row = st.sent.find((r) => r.id === 'CLM-0702');
    if (row) {
      row.status = 'returned';
      row.happening =
        'USPS returned it: no such PO box. ELVA checked the address on Aetna’s dispute page and sent it again on Oct 10.';
      delete row.trackingLink;
    }
    return st;
  }
  if (scenario === 'autopilot') {
    settings.autopilot = true;
    settings.confirmed = 20;
    return st;
  }
  return st;
}

/** Ids whose rows were pushed in this session (so a Cancel can remove them). */
const sessionSent = new Set<string>();

const state: State = initState();

// ---- subscription ----
export function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}
export function getVersion(): number {
  return version;
}
export function useAppealsVersion(): number {
  return useSyncExternalStore(subscribe, getVersion, getVersion);
}

// ---- getters ----
export function getScenarioSync(): Scenario {
  return state.scenario;
}
export function getDeniedSync(): Claim[] {
  return state.denied.filter((c) => !c.removed);
}
export function getUnderpaidSync(): Claim[] {
  return state.underpaid.filter((c) => !c.removed && !c.reprocessRef);
}
export function getSentSync(): AppealSent[] {
  return state.sent;
}
export function getClaimSync(id: string): Claim | null {
  return state.denied.find((c) => c.id === id) ?? state.underpaid.find((c) => c.id === id) ?? null;
}
export function getSentRowSync(id: string): AppealSent | null {
  return state.sent.find((r) => r.id === id) ?? null;
}
export function getAppealSettings(): AppealSettings {
  return state.settings;
}
export function saveAppealSettings(patch: Partial<AppealSettings>): void {
  Object.assign(state.settings, patch);
  emit();
}

export function deniedTotalSync(): number {
  return getDeniedSync().reduce((s, c) => s + billedOf(c.procedures), 0);
}
export function underpaidShortSync(): number {
  return getUnderpaidSync().reduce((s, c) => s + shortOf(c), 0);
}
export function sentOpenCountSync(): number {
  return state.sent.filter((r) => !r.closed && !isDecided(r)).length;
}
/** "Needs you" across Denied and Underpaid: a question only a person can answer. The band's amber em reads this. */
export function needsYouCountSync(): number {
  return [...getDeniedSync(), ...getUnderpaidSync()].filter((c) => c.answer === 'needs_you').length;
}
/** Ready to send: ELVA prepared the appeal, resend or reprocess request; a person only presses send. */
export function readyToSendCountSync(): number {
  return [...getDeniedSync(), ...getUnderpaidSync()].filter(
    (c) => c.answer === 'appeal' || c.answer === 'resend' || c.answer === 'reprocess',
  ).length;
}
/** Sidebar danger count (D204): needs you + ready to send. */
export function waitingCountSync(): number {
  return needsYouCountSync() + readyToSendCountSync();
}
/** Reactive hook for the sidebar danger-soft count. */
export function useAppealsWaitingCount(): number {
  return useSyncExternalStore(subscribe, waitingCountSync, waitingCountSync);
}

/** Expected total for an underpaid claim, respecting the remaining maximum. */
export function expectedTotalOf(c: Claim): number {
  if (!c.table) return 0;
  return c.table.reduce((s, row) => s + expectedFor(row, c.planRemaining ?? Infinity).expected, 0);
}
export function shortOf(c: Claim): number {
  if (c.paid == null) return 0;
  return expectedTotalOf(c) - c.paid;
}

const decidedIds = new Set<string>();
function isDecided(r: AppealSent): boolean {
  return decidedIds.has(r.id);
}
export function isSentDecided(r: AppealSent): boolean {
  return isDecided(r);
}

function pushHistory(claim: Claim, entry: string): void {
  claim.history = [...claim.history, entry];
}

// ---- open (history) ----
export function touchOpen(id: string): void {
  const claim = getClaimSync(id);
  if (claim && !openedOnce.has(id) && !claim.removed) {
    openedOnce.add(id);
    pushHistory(claim, `opened by ${who} today`);
    emit();
  }
}

// ---- send blocked reason (§8) ----
export function sendBlockedReason(claim: Claim, settings: AppealSettings): string | null {
  if (!settings.authorization.signed) {
    return 'Your claims and appeals authorization isn’t signed';
  }
  if (claim.deadline && claim.deadline.date < '2026-10-15') {
    return 'The appeal deadline has passed';
  }
  if (claim.needsHuman) {
    return `${claim.payer.short} takes appeals by mail only and the deadline is close — this is with Human in the loop`;
  }
  if (claim.stepFirst) {
    return `${claim.payer.short} needs ${claim.stepFirst.toLowerCase()} first`;
  }
  // signature on file for the dentist who signs
  const signer = shortDentist(claim.provider);
  const dentist = settings.dentists.find((d) => signer.includes(d.name.split(' ').pop() ?? ''));
  if (dentist && !dentist.onFile) {
    return `No signature on file for ${signer}`;
  }
  if (claim.appealKind === 'clinical' && claim.approval !== 'approved') {
    return `Waiting for ${claim.approvedBy ?? signer} to approve`;
  }
  if (claim.appealKind === 'patient' && claim.patientForm !== 'signed') {
    return `Waiting for ${claim.firstName} to sign ${claim.payer.short}’s form`;
  }
  return null;
}

// ---- send the appeal (§8) ----
export function sendAppeal(id: string): void {
  const claim = getClaimSync(id);
  if (!claim) return;
  claim.removed = 'sent';
  sessionSent.add(id);

  const preset = PRELOADED.find((r) => r.id === id);
  let row: AppealSent;
  if (preset) {
    row = { ...preset };
  } else {
    const rule = ruleForPayerName(claim.payer.name);
    const first = rule.order[0] ?? rule.accepts[0] ?? 'certified_mail';
    row = buildRowForClaim(claim, statusForFirstChannel(first));
  }
  // The after card reads the send time and channel from here (rcm-30 §3).
  claim.resolution = {
    at: clockNow(),
    by: APPEALS_USER.name,
    reason: 'Appeal sent',
    via: row.sentHow,
  };
  // remove any stale row with this id, then push
  state.sent = state.sent.filter((r) => r.id !== id);
  state.sent.push(row);
  emit();
}

export function cancelSend(id: string): void {
  const claim = getClaimSync(id);
  if (claim) delete claim.removed;
  sessionSent.delete(id);
  state.sent = state.sent.filter((r) => r.id !== id);
  emit();
}

// ---- who-signs flows (D130) ----
export function requestDentistApproval(id: string): void {
  const c = getClaimSync(id);
  if (!c) return;
  c.approval = 'waiting';
  pushHistory(c, `ELVA texted ${c.approvedBy ?? shortDentist(c.provider)} the letter ${clockNow()}`);
  emit();
}
export function approveLetter(id: string): void {
  const c = getClaimSync(id);
  if (!c) return;
  c.approval = 'approved';
  c.approvedBy = c.approvedBy ?? shortDentist(c.provider);
  c.approvedOn = 'Oct 15';
  pushHistory(c, `${c.approvedBy} approved the letter ${clockNow()}`);
  emit();
}
export function resetApproval(id: string): void {
  const c = getClaimSync(id);
  if (!c) return;
  if (c.appealKind !== 'clinical') return;
  c.approval = 'waiting';
  emit();
}
export function requestPatientForm(id: string): void {
  const c = getClaimSync(id);
  if (!c) return;
  c.patientForm = 'waiting';
  c.patientFormDate = 'Oct 15';
  pushHistory(c, `ELVA texted ${c.firstName} the form ${clockNow()}`);
  emit();
  setTimeout(() => {
    c.patientForm = 'signed';
    pushHistory(c, `${c.firstName} signed the form on her phone`);
    emit();
  }, LATENCY.approve);
}

// ---- denied actions ----
export function confirmResend(id: string): void {
  const c = state.denied.find((x) => x.id === id);
  if (c) {
    c.removed = 'resent';
    c.resolution = { at: clockNow(), by: APPEALS_USER.name, reason: 'Resent to Claims to review' };
    emit();
  }
}

/**
 * Every "What happens to the $X?" step ends here (rcm-30 §4). The outcome decides
 * where the money goes; the claim leaves the queue and its after card stays on screen.
 */
export function resolveClaim(id: string, outcome: Outcome, reason: string, note?: string): void {
  const c = getClaimSync(id);
  if (!c) return;
  c.removed = outcome === 'patient' || outcome === 'other_insurance' ? 'billed' : 'written_off';
  c.resolution = {
    at: clockNow(),
    by: APPEALS_USER.name,
    reason,
    ...(note ? { note } : {}),
  };
  pushHistory(c, `${reason} · ${APPEALS_USER.name} ${clockNow()}`);
  emit();
}
export function restore(id: string): void {
  const c = getClaimSync(id);
  if (c) {
    delete c.removed;
    delete c.reprocessRef;
    delete c.resolution;
    if (sessionSent.has(id)) {
      sessionSent.delete(id);
      state.sent = state.sent.filter((r) => r.id !== id);
    }
    emit();
  }
}

// ---- Sofia decision (D153) ----
/** Staff record the patient's answer. ELVA never texts the patient (D153). */
export function decideSofia(id: string, optionId: string, note?: string): void {
  const c = state.denied.find((x) => x.id === id);
  if (!c?.decision) return;
  const opt = c.decision.options.find((o) => o.id === optionId);
  if (!opt) return;
  const recorded = opt.needsNote && note ? `${opt.recorded}: ${note}` : opt.recorded;
  c.decision = { ...c.decision, chosen: optionId };
  c.decisionRecord = { recorded, at: clockNow(), by: APPEALS_USER.name };
  pushHistory(c, `${recorded} · recorded by ${APPEALS_USER.name} ${clockNow()}`);
  if (opt.resolvesTo === 'write_off') {
    c.removed = 'written_off';
    c.resolution = {
      at: clockNow(),
      by: APPEALS_USER.name,
      reason: `Written off ${money(billedOf(c.procedures))} — Delta’s frequency limit, no waiver on file.`,
    };
  } else {
    applySofiaAppeal(c, optionId);
  }
  emit();
}

function applySofiaAppeal(c: Claim, chosenId: string): void {
  c.answer = 'appeal';
  if (c.decision) c.decision = { ...c.decision, chosen: chosenId };
  c.deadline = SOFIA_APPEAL.deadline;
  c.appealKind = SOFIA_APPEAL.appealKind;
  c.recommendationHeader = SOFIA_APPEAL.recommendationHeader;
  c.recommendationBody = SOFIA_APPEAL.recommendationBody;
  c.packet = SOFIA_APPEAL.packet;
  c.letter = SOFIA_APPEAL.letter;
}

// ---- underpaid reprocess ----
function pushReprocessRow(c: Claim, ref: string): void {
  state.sent.push({
    id: c.id,
    patient: c.patient,
    payerShort: c.payer.short,
    codes: collapseCodes(c.procedures),
    billed: billedOf(c.procedures),
    sentDate: '2026-10-15',
    sentDisplay: 'Oct 15',
    sentHow: `${c.payer.short}’s provider line`,
    status: 'reprocessing',
    happening: `${c.payer.short} is reprocessing it · reference ${ref}.`,
  });
  sessionSent.add(c.id);
}

export function haveElvaCall(id: string): string {
  const c = state.underpaid.find((x) => x.id === id);
  const ref = 'R-26-55810';
  if (c) {
    c.reprocessRef = ref;
    pushReprocessRow(c, ref);
    emit();
  }
  return ref;
}
export function saveReprocessRef(id: string, ref: string): void {
  const c = state.underpaid.find((x) => x.id === id);
  if (c) {
    c.reprocessRef = ref;
    pushReprocessRow(c, ref);
    emit();
  }
}
export function acceptPaid(id: string, reason: string, note?: string): void {
  const c = state.underpaid.find((x) => x.id === id);
  if (c) {
    c.removed = 'accepted';
    c.resolution = {
      at: clockNow(),
      by: APPEALS_USER.name,
      reason,
      ...(note ? { note } : {}),
    };
    emit();
  }
}

// ---- appeals-sent decide ----
export function decideSent(id: string): void {
  decidedIds.add(id);
  const r = state.sent.find((x) => x.id === id);
  if (r) r.closed = true;
  emit();
}

// ---- cross-feature: an item appealed from Posting (POSTING_HANDOFF §4.2) ----
export function addDeniedFromPosting(input: {
  id: string;
  patient: string;
  firstName: string;
  payer: { name: string; short: string };
  memberId: string;
  reason: string;
}): void {
  if (state.denied.some((c) => c.id === input.id)) return;
  const claim: Claim = {
    id: input.id,
    kind: 'denied',
    patient: input.patient,
    firstName: input.firstName,
    payer: input.payer,
    memberId: input.memberId,
    serviceDate: 'Sep 29, 2026',
    provider: 'Dr. Emily Carter',
    procedures: [{ code: 'D2740', description: 'Crown, porcelain/ceramic', tooth: '#30', fee: 1180 }],
    deniedDate: 'Oct 15, 2026',
    answer: 'appeal',
    appealKind: 'billing',
    why: {
      eyebrow: `Why ${input.payer.short} paid it this way`,
      headline: input.reason,
      metaLine: `Sent to appeals from Posting · ${clockNow()}`,
      sources: [],
    },
    deadline: { date: '2027-04-13', basis: `${input.payer.short} allows 180 days from its decision`, verb: 'Appeal' },
    recommendationHeader: 'ELVA can prepare an appeal',
    recommendationBody:
      'This came from Posting. ELVA will gather the X-ray, the clinical note and the original claim, and draft the appeal letter.',
    recommendationSources: [],
    packet: [],
    letter: {
      text: 'To the Appeals Team,\n\nWe are appealing the processing of this claim. The enclosed records support the treatment as billed.',
      basis: 'the record',
    },
    history: [`Moved from Posting ${clockNow()}`],
  };
  state.denied.unshift(claim);
  emit();
}
